<template>
	<SlidingSheet
		:is-open="isOpen"
		:title="sheetTitle"
		width="800px"
		@close="$emit('close')"
	>
		<template #content>
			<div
				v-if="channelHistory.length === 0"
				class="pa-4"
			>
				<v-alert type="info">
					No history found for this channel.
				</v-alert>
			</div>

			<v-expansion-panels
				v-else
				v-model="expandedPanels"
				multiple
				flat
				class="channel-history-panels"
			>
				<v-expansion-panel
					v-for="(preference, index) in channelHistory"
					:key="preference.preferenceId || index"
					class="dense-panel mb-2"
				>
					<v-expansion-panel-header class="py-1">
						<div class="d-flex align-center justify-space-between flex-grow-1 pr-4">
							<div>
								<span class="font-weight-medium">
									{{ preference.dateSubmitted }}
								</span>
								<v-chip
									:color="preference.isCurrent ? 'success' : 'default'"
									x-small
									class="ml-2"
								>
									{{ preference.isCurrent ? 'Current' : 'Historic' }}
								</v-chip>
							</div>
							<v-chip
								:color="getPreferenceColor(preference.preferenceValue)"
								x-small
								dark
							>
								{{ preference.preferenceValue }}
							</v-chip>
						</div>
					</v-expansion-panel-header>

					<v-expansion-panel-content>
						<v-simple-table
							dense
							class="preference-details-table"
						>
							<template #default>
								<tbody>
									<tr>
										<td
											class="font-weight-medium"
											style="width: 30%;"
										>
											Brand
										</td>
										<td>
											{{ preference.brandName || '-' }}
										</td>
									</tr>
									<tr>
										<td class="font-weight-medium">
											Channel
										</td>
										<td>
											{{ preference.channelName || '-' }}
										</td>
									</tr>
									<tr v-if="preference.parentChannelName">
										<td class="font-weight-medium">
											Parent Channel
										</td>
										<td>
											{{ preference.parentChannelName }}
										</td>
									</tr>
									<tr>
										<td class="font-weight-medium">
											Date Submitted
										</td>
										<td>
											{{ preference.dateSubmitted }}
										</td>
									</tr>
									<tr>
										<td class="font-weight-medium">
											Valid From
										</td>
										<td>
											{{ preference.validFrom || '-' }}
										</td>
									</tr>
									<tr>
										<td class="font-weight-medium">
											Valid To
										</td>
										<td>
											{{ preference.validTo || 'No end date' }}
										</td>
									</tr>
									<tr v-if="preference.sourceCode">
										<td class="font-weight-medium">
											Source Code
										</td>
										<td>
											{{ preference.sourceCode }}
										</td>
									</tr>
									<tr v-if="preference.responseChannel">
										<td class="font-weight-medium">
											Response Channel
										</td>
										<td>
											{{ preference.responseChannel }}
										</td>
									</tr>
									<tr v-if="preference.divId">
										<td class="font-weight-medium">
											Source
										</td>
										<td>
											{{ preference.divId }}
										</td>
									</tr>
									<tr v-if="preference.privacyPolicyName">
										<td class="font-weight-medium">
											Privacy Policy
										</td>
										<td>
											{{ preference.privacyPolicyName }}
										</td>
									</tr>
									<tr v-if="preference.submissionId">
										<td class="font-weight-medium">
											Submission ID
										</td>
										<td>
											{{ preference.submissionId }}
										</td>
									</tr>
								</tbody>
							</template>
						</v-simple-table>

						<div
							v-if="preference.statementText"
							class="mt-4"
						>
							<div class="font-weight-medium mb-2">
								Statement Text
							</div>
							<v-textarea
								:value="preference.statementText"
								outlined
								readonly
								filled
								auto-grow
								hide-details
							/>
						</div>

						<div
							v-if="preference.extendedPreferences && preference.extendedPreferences.length > 0"
							class="mt-4"
						>
							<div class="font-weight-medium mb-2">
								Extended Preferences
							</div>
							<v-simple-table
								dense
							>
								<template #default>
									<thead>
										<tr>
											<th class="text-left">
												Key
											</th>
											<th class="text-left">
												Value
											</th>
										</tr>
									</thead>
									<tbody>
										<tr
											v-for="(extPref, extIndex) in preference.extendedPreferences"
											:key="extIndex"
										>
											<td>
												{{ extPref.key }}
											</td>
											<td>
												{{ extPref.value }}
											</td>
										</tr>
									</tbody>
								</template>
							</v-simple-table>
						</div>
					</v-expansion-panel-content>
				</v-expansion-panel>
			</v-expansion-panels>
		</template>
	</SlidingSheet>
</template>

<script>
import SlidingSheet from '../../../../../../../shared/components/sliding-sheet.vue'

export default {
	name: 'channel-detailed-history',
	components: {
		SlidingSheet
	},
	props: {
		isOpen: {
			type: Boolean,
			default: false
		},
		channelName: {
			type: String,
			default: ''
		},
		selectedPreferenceId: {
			type: Number,
			default: null
		},
		channelPreferences: {
			type: Array,
			default: () => []
		}
	},
	data () {
		return {
			expandedPanels: []
		}
	},
	computed: {
		sheetTitle () {
			return `${this.channelName} - History`
		},
		channelHistory () {
			return this.channelPreferences
		}
	},
	watch: {
		isOpen: {
			immediate: true,
			handler (newVal) {
				if (newVal) {
					this.setDefaultExpandedPanel()
				}
			}
		},
		channelHistory: {
			immediate: true,
			handler () {
				if (this.isOpen) {
					this.setDefaultExpandedPanel()
				}
			}
		}
	},
	mounted () {
		document.addEventListener('keydown', this.handleEscapeKey)
	},
	beforeDestroy () {
		document.removeEventListener('keydown', this.handleEscapeKey)
	},
	methods: {
		setDefaultExpandedPanel () {
			this.$nextTick(() => {
				this.expandedPanels = [this.findDefaultExpandedIndex()]
				this.scrollToExpandedPanel()
			})
		},
		scrollToExpandedPanel () {
			this.$nextTick(() => {
				setTimeout(() => {
					const expandedIndex = this.expandedPanels[0]
					if (expandedIndex !== undefined) {
						const panels = this.$el.querySelectorAll('.v-expansion-panel')
						const targetPanel = panels[expandedIndex]
						if (targetPanel) {
							targetPanel.scrollIntoView({ behavior: 'smooth', block: 'center' })
						}
					}
				}, 300)
			})
		},
		findDefaultExpandedIndex () {
			if (this.selectedPreferenceId) {
				const index = this.channelHistory.findIndex(
					pref => pref.preferenceId === this.selectedPreferenceId
				)
				return index >= 0 ? index : 0
			}
			return 0
		},
		getPreferenceColor (value) {
			if (value === 'Opt-In') return 'success'
			if (value === 'Opt-Out') return 'error'
			return 'grey'
		},
		handleEscapeKey (event) {
			if (event.key === 'Escape' && this.isOpen) {
				this.$emit('close')
			}
		}
	}
}
</script>

<style scoped>
.channel-history-panels {
	margin: 0;
}

.dense-panel {
	border: 1px solid var(--global-border-color);
	border-radius: var(--radius-sm, 4px);
}

.dense-panel >>> .v-expansion-panel-header {
	min-height: 40px;
	padding: 6px 12px;
	font-size: 0.8125rem;
}

.dense-panel >>> .v-expansion-panel-header .v-chip {
	font-size: 0.6875rem;
	height: 20px;
}

.dense-panel >>> .v-expansion-panel-content__wrap {
	padding: 8px 12px 12px;
	font-size: 0.8125rem;
}

.preference-details-table td {
	padding: 4px 8px !important;
	font-size: 0.75rem;
}

.preference-details-table .font-weight-medium {
	font-size: 0.75rem;
}

.preference-details-table tr:nth-child(odd) {
	background-color: var(--global-main-background-color);
}

.dense-panel >>> .v-textarea {
	font-size: 0.75rem;
}

.dense-panel >>> .font-weight-medium {
	font-size: 0.8125rem;
}
</style>
